import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MainLayout1 from "Components/UI/Layout/MainLayout1";

import {
  endcustomeraccountConnectOID,
  getUnplacedAccounts,
  matchSearch,
  searchEndCucstomerAccountView,
  searchInvoices,
} from "API/swesum";

import EndcustomerUnplacedTable from "Components/Account/EndcustomerUnplacedTable";
import { useAccountCharts } from "Context/AccountCharts";
import InvoiceControls from "./components/InvoiceControls";
import { usePopupNotificationContext } from "Context/NotificationPopup";

export default function EndcustomerUnplaced() {
  const { fetchAccountCharts, accountCharts } = useAccountCharts();
  const [unplacedAccounts, setUnplacedAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [totalRows, setTotalRows] = useState(0);
  const { openError, openSuccess } = usePopupNotificationContext();

  const asyncFetchSearchResults = async (searchTerm) => {
    setSearchLoading(true);
    const res = await searchEndCucstomerAccountView(searchTerm);
    setSearchResults(res.data);
    setSearchLoading(false);
  };

  const resetModalView = () => {
    setSearchResults([]);
  };

  const asyncConnectInvoice = async (searchId, amount) => {
    setConnectLoading(true);
    try {
      const res = await endcustomeraccountConnectOID(
        selectedId,
        searchId,
        null,
        null,
        amount
      );

      if (res.status === 201) {
        resetModalView();
        setSelectedId(null);
        asyncFetchData();
        openSuccess(res.data.Message, 3000);
      }
    } catch (err) {
      if (process.env.REACT_APP_ENV === "DEV") {
        console.error("Connect invoice chart error:", err);
      }
      openError(err.response.data, 3000);
    } finally {
      setConnectLoading(false);
    }
  };

  const asyncConnectInvoicesAccountChart = async (
    accountChartId,
    clientId = null,
    amount = 0,
    bookingDate
  ) => {
    setConnectLoading(true);

    try {
      const res = await endcustomeraccountConnectOID(
        selectedId,
        null,
        accountChartId,
        clientId,
        amount,
        bookingDate
      );

      if (res.status === 201) {
        resetModalView();
        setSelectedId(null);
        asyncFetchData();
        openSuccess(res.data.Message, 3000);
      }
    } catch (err) {
      if (process.env.REACT_APP_ENV === "DEV") {
        console.error("Connect invoice chart error:", err);
      }
      openError(err.response.data, 3000);
    } finally {
      setConnectLoading(false);
    }
  };

  useEffect(() => {
    resetModalView();
  }, [selectedId]);

  const asyncFetchData = async () => {
    const data = await getUnplacedAccounts();
    setUnplacedAccounts(data);
    setTotalRows(data.Count);
  };

  useEffect(() => {
    const init = () => {
      asyncFetchData();
      if (!accountCharts || accountCharts.length === 0) {
        fetchAccountCharts();
      }
    };
    init();
  }, []);

  const initAmount = useMemo(() => {
    const account = unplacedAccounts.find((acc) => acc.Id === selectedId);
    return account ? Math.abs(account.Amount) : "";
  }, [unplacedAccounts, selectedId]);

  return (
    <>
      <MainLayout1>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "1.0rem",
          }}
        >
          <Typography variant="h5">Oplacerat</Typography>
        </Box>

        <EndcustomerUnplacedTable
          accounts={unplacedAccounts}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          totalRows={totalRows}
          onChangeRowsPerPage={(value) => setRowsPerPage(value)}
          onChangePage={(value) => setCurrentPage(value)}
          onRowClick={(paymentId) => setSelectedId(paymentId)}
        />
        {!!selectedId ? (
          <InvoiceControls
            initialAmount={initAmount}
            onClose={() => setSelectedId(null)}
            accountCharts={accountCharts}
            connectInvoicesSearch={asyncConnectInvoice}
            connectInvoicesAccountChart={asyncConnectInvoicesAccountChart}
            onSearch={asyncFetchSearchResults}
            searchResults={searchResults}
            searchLoading={searchLoading}
            connectLoading={connectLoading}
          />
        ) : null}
      </MainLayout1>
    </>
  );
}
